// =============================================================================
// SHARED I18N STORE
// =============================================================================
// Zustand store for translations - fetches from database via API
// Supports any agent type via configuration
// =============================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { reportI18nMiss } from './miss';

/**
 * The languages that SHIP IN THE BUNDLE. Closed on purpose: SUPPORTED_LANGUAGES
 * below is a Record over this, so adding a code here without adding its entry
 * is a compile error, and shared/i18n/supportedLanguages.cjs parses that same
 * literal for every Node-side consumer.
 */
export type BuiltInLanguageCode =
  'en' | 'es' | 'zh-CN' | 'zh-TW' | 'ht' | 'vi' | 'ko' | 'ar' | 'tl' | 'bn' | 'pt' | 'ru' | 'so';

/**
 * A language the platform may be asked to speak — OPEN, deliberately.
 *
 * It was the closed union above, and that union was the hardest of the several
 * places thirteen was baked in: not a list to update but a COMPILE-TIME BOUND.
 * A district could not enable Karen, Pashto or Marshallese at runtime however
 * the database was configured, because `setLanguage` would not accept the code.
 *
 * Now that `fill-translations.yml` fills a PR's locales through the real
 * generator — which already takes `--lang <code>` for exactly one language —
 * the marginal cost of the fourteenth locale is near zero. The constraint stops
 * being what we pre-built and becomes what a district's families speak, so the
 * type has to admit a code the bundle has never seen.
 *
 * `(string & {})` is the TypeScript idiom that keeps autocomplete for the
 * thirteen while admitting any string: a plain `| string` would collapse the
 * union and lose the suggestions that make the built-ins discoverable.
 */
export type LanguageCode = BuiltInLanguageCode | (string & {});

/**
 * PLATFORM DEFAULTS — the thirteen the bundle carries, and the fallback for a
 * tenant that has configured nothing. A district's own set comes from
 * `GET /api/i18n/languages`, which reads district_languages (migration 743).
 *
 * Still a Record over the CLOSED type, so this map stays exhaustive: the point
 * of widening LanguageCode was to admit runtime languages, not to make it
 * possible to forget one of the built-ins.
 */
export const SUPPORTED_LANGUAGES: Record<BuiltInLanguageCode, { name: string; nativeName: string; rtl?: boolean }> = {
  'en': { name: 'English', nativeName: 'English' },
  'es': { name: 'Spanish', nativeName: 'Español' },
  'zh-CN': { name: 'Chinese (Simplified)', nativeName: '简体中文' },
  'zh-TW': { name: 'Chinese (Traditional)', nativeName: '繁體中文' },
  'ht': { name: 'Haitian Creole', nativeName: 'Kreyòl Ayisyen' },
  'vi': { name: 'Vietnamese', nativeName: 'Tiếng Việt' },
  'ko': { name: 'Korean', nativeName: '한국어' },
  'ar': { name: 'Arabic', nativeName: 'العربية', rtl: true },
  'tl': { name: 'Tagalog', nativeName: 'Tagalog' },
  'bn': { name: 'Bengali', nativeName: 'বাংলা' },
  'pt': { name: 'Portuguese', nativeName: 'Português' },
  'ru': { name: 'Russian', nativeName: 'Русский' },
  'so': { name: 'Somali', nativeName: 'Soomaali' },
};

interface I18nState {
  language: LanguageCode;
  translations: Record<string, string>;
  loading: boolean;
  error: string | null;
  lastFetched: string | null;
  cachedForLanguage: LanguageCode | null;
  agentType: string | null;

  // Translation function - returns translated string, fallback, or key
  t: (key: string, fallbackOrVars?: string | Record<string, string | number>, vars?: Record<string, string | number>) => string;

  setLanguage: (lang: LanguageCode) => void;
  setTranslations: (translations: Record<string, string>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setAgentType: (agent: string) => void;
  fetchTranslations: (agent: string, lang: LanguageCode, apiBase: string) => Promise<void>;
}

/**
 * RUNTIME LANGUAGES — the ones a district turned on that the bundle never knew.
 *
 * SUPPORTED_LANGUAGES answers "what shipped". It cannot answer "is Pashto
 * right-to-left", because Pashto is not in it. Widening LanguageCode without
 * this would have compiled and then rendered Pashto and Urdu left-to-right —
 * the bug the type error surfaced.
 *
 * Filled from `GET /api/i18n/languages`, which reads district_languages joined
 * to supported_languages (migration 743), so the direction comes from the
 * catalogue rather than from a guess about the script.
 */
const _runtimeLanguages: Record<string, { name: string; nativeName: string; rtl?: boolean }> = {};

export function registerRuntimeLanguages(
  langs: Array<{ language_code: string; language_name?: string; native_name?: string; is_rtl?: boolean }>,
) {
  for (const l of (langs || [])) {
    if (!l || !l.language_code) continue;
    _runtimeLanguages[l.language_code] = {
      name: l.language_name || l.language_code,
      nativeName: l.native_name || l.language_name || l.language_code,
      rtl: l.is_rtl === true,
    };
  }
}

/**
 * A language's display facts, built-in or runtime. Returns undefined for a code
 * nothing has described — a caller that must render something should fall back
 * to the code itself rather than to English.
 */
export function getLanguageMeta(code: LanguageCode) {
  return (SUPPORTED_LANGUAGES as Record<string, { name: string; nativeName: string; rtl?: boolean }>)[code]
      ?? _runtimeLanguages[code];
}

/**
 * Direction for any language, including one added after this bundle was built.
 * Defaults to LTR, which is the safe wrong answer: an RTL language rendered LTR
 * is awkward, an LTR language rendered RTL is unreadable.
 */
export function isRtlLanguage(code: LanguageCode): boolean {
  return getLanguageMeta(code)?.rtl === true;
}

// =============================================================================
// LOCALE REGISTRY — each agent registers its own bundled JSON via registerLocales()
// =============================================================================
// Each agent calls registerLocales() at module level (before any component renders).
// The agent's i18n wrapper uses import.meta.glob to bundle its locale files within
// its own Vite project scope, then passes the result here.
//
// This avoids the cross-root glob that previously failed when Vite couldn't resolve
// import.meta.glob('./locales/**/*.json') from shared/ across workspace boundaries.
// =============================================================================

// Structure: { agent: { lang: { key: translation } } }
const _agentLocales: Record<string, Record<string, Record<string, string>>> = {};

/**
 * Register bundled locale translations for an agent.
 * Call at module level in each agent's i18n wrapper — runs before any component renders.
 *
 * MERGES per language, so an agent can register more than one bundle into the same
 * namespace — its own `locales/<agent>/*.json` PLUS the cross-agent `locales/shared/*.json`
 * (the keys used by shared/ components like cobrowse and calendar). Later registrations
 * win on key collisions, so register the agent's own bundle first and `shared` second only
 * if you want shared to override — here English values are identical, so order is inert.
 *
 * @param agent  Agent identifier (e.g. 'fafsa', 'parent-advocate', 'benefits', 'admin')
 * @param rawGlob  Result of import.meta.glob('.../*.json', { eager: true, import: 'default' })
 *                 Keys are file paths like '../../../../shared/i18n/locales/fafsa/es.json'
 */
export function registerLocales(agent: string, rawGlob: Record<string, unknown>) {
  if (!_agentLocales[agent]) _agentLocales[agent] = {};
  for (const [filePath, translations] of Object.entries(rawGlob)) {
    const match = filePath.match(/\/([^/]+)\.json$/);
    if (match) {
      const lang = match[1];
      // Merge, don't overwrite — the shared bundle registers on top of the agent's own.
      _agentLocales[agent][lang] = {
        ..._agentLocales[agent][lang],
        ...(translations as Record<string, string>),
      };
    }
  }
}

function _getLocale(agent: string, lang: string): Record<string, string> {
  return _agentLocales[agent]?.[lang] ?? {};
}

/**
 * Does this agent SHIP a bundle for this language?
 *
 * The answer decides whether a screen can render before the network answers.
 * For a bundled language the strings are on disk and the first paint is
 * correct. For a language a DISTRICT enabled — which by definition has no
 * bundle — every key misses until `fetchTranslations` returns the tenant
 * overrides, and `t()` has no English fallback, so the family's first paint is
 * a screenful of `⟦benefits:ksw "entry.district.title"⟧`.
 *
 * That is the same failure as an unreadable door, arriving a beat later: the
 * families this tier exists to serve are exactly the ones who would see it.
 * Callers use this with `cachedForLanguage` to hold the first paint until the
 * words exist. Found by the Karen proof, which threw here rather than passing.
 */
export function hasBundledLocale(agent: string, lang: string): boolean {
  const bundle = _agentLocales[agent]?.[lang];
  return !!bundle && Object.keys(bundle).length > 0;
}

export const createI18nStore = (storeName: string) => {
  // Derive agent type from store name: 'fafsa-i18n' → 'fafsa', 'parent-advocate-i18n' → 'parent-advocate'
  const derivedAgentType = storeName.replace(/-i18n$/, '');

  return create<I18nState>()(
  persist(
    (set, get) => ({
      language: 'en' as LanguageCode,
      translations: {} as Record<string, string>,
      loading: false,
      error: null,
      lastFetched: null,
      cachedForLanguage: null,
      agentType: derivedAgentType,  // Set immediately — t() works on first render

      t: (key: string, fallbackOrVars?: string | Record<string, string | number>, vars?: Record<string, string | number>) => {
        const state = get();

        let variables: Record<string, string | number> | undefined;
        if (typeof fallbackOrVars === 'object' && fallbackOrVars !== null) {
          variables = fallbackOrVars;
        } else if (vars) {
          variables = vars;
        }

        // Lookup order (Step-0 PR-0C-2 — no fallback chain):
        // 1. Tenant overrides (district customizations, loaded via fetchTranslations)
        // 2. Bundled locale JSON for the current language
        // 3. fail — no English-bundle step, no inline-string fallback
        const text =
          state.translations[key] ||
          (state.agentType ? _getLocale(state.agentType, state.language)[key] : undefined);

        if (!text) {
          // Single shared failure handler (dev-throw / prod-marker + /api/i18n/miss →
          // CloudWatch) — the SAME reportI18nMiss the enum maps use, not a second copy of
          // the rule. A string second argument (a legacy inline fallback) is intentionally
          // ignored; the completeness gate guarantees every referenced key exists per locale.
          return reportI18nMiss(`${state.agentType ?? 'i18n'}:${state.language} "${key}"`);
        }

        if (variables) {
          let result = text;
          Object.entries(variables).forEach(([varKey, value]) => {
            result = result.replace(new RegExp(`\\{\\{?${varKey}\\}\\}?`, 'g'), String(value));
          });
          return result;
        }

        return text;
      },

      setLanguage: (language: LanguageCode) => set({ language }),
      setTranslations: (translations: Record<string, string>) => set({ translations, lastFetched: new Date().toISOString() }),
      setLoading: (loading: boolean) => set({ loading }),
      setError: (error: string | null) => set({ error }),
      setAgentType: (agentType: string) => set({ agentType }),

      fetchTranslations: async (agent: string, lang: LanguageCode, apiBase: string) => {
        const state = get();

        // Base translations are bundled — no API call needed for base case.
        // Only fetch if language changed (to pick up tenant overrides for new lang).
        if (
          state.agentType === agent &&
          state.cachedForLanguage === lang &&
          state.lastFetched
        ) {
          const cacheAge = Date.now() - new Date(state.lastFetched).getTime();
          if (cacheAge < 5 * 60 * 1000) return; // 5-min cache
        }

        set({ loading: true, error: null, agentType: agent, language: lang });

        // Fetch tenant overrides only — much smaller payload than full translation set.
        // Returns {} if no overrides exist for this tenant/agent/lang combination.
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 8000);

          const response = await fetch(`${apiBase}/api/i18n/${agent}/${lang}/overrides`, {
            signal: controller.signal,
          });
          clearTimeout(timeoutId);

          if (response.ok) {
            const data = await response.json();
            const overrides = data.overrides ?? data.translations ?? {};
            set({
              translations: overrides,
              cachedForLanguage: lang,
              loading: false,
              lastFetched: new Date().toISOString(),
            });
          } else {
            // No overrides endpoint or 404 = no overrides. Use bundled JSON only.
            set({ translations: {}, cachedForLanguage: lang, loading: false, lastFetched: new Date().toISOString() });
          }
        } catch {
          // Network error: fall back to bundled JSON silently.
          set({ translations: {}, loading: false, cachedForLanguage: lang, lastFetched: new Date().toISOString() });
        }
      },
    }),
    {
      name: storeName,
      version: 8,
      migrate: (persistedState: unknown, _version: number) => {
        // v6→v7: Force clean state to clear stale localStorage.
        // agentType is always derived from storeName — never stored.
        const state = persistedState as Partial<I18nState>;
        return {
          language: state.language ?? 'en' as LanguageCode,
          translations: {} as Record<string, string>,
          lastFetched: null as string | null,
          cachedForLanguage: null as LanguageCode | null,
          agentType: derivedAgentType,  // Always from storeName, never from storage
        };
      },
      merge: (persistedState, currentState) => ({
        // Merge persisted into current, but NEVER let persisted agentType win —
        // it may be null (pre-fix) or stale. Always use the derived value.
        ...currentState,
        ...(persistedState as Partial<I18nState>),
        agentType: derivedAgentType,
        translations: {},         // Never restore — bundled JSON is canonical
        lastFetched: null,
        cachedForLanguage: null,
      }),
      partialize: (state: I18nState) => ({
        language: state.language,           // Only persist language preference
        // agentType intentionally excluded — always derived from storeName
      }),
    }
  )
);
};

// Pre-created stores for known agents
export const useFafsaI18nStore = createI18nStore('fafsa-i18n');
export const useParentAdvocateI18nStore = createI18nStore('parent-advocate-i18n');
export const useBenefitsI18nStore = createI18nStore('benefits-i18n');
export const useAdminI18nStore = createI18nStore('admin-i18n');
